import React, { useEffect, useState } from "react";
import { Box, Stack, Button, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutorenewIcon from "@mui/icons-material/Autorenew";

import PageHeader from "../../components/common/PageHeader";
import DataTable from "../../components/common/DataTable";
import StatusChip from "../../components/common/StatusChip";

import {
  REPORT_STRINGS,
  COMMON_STRINGS,
  STATUS_CONFIG,
} from "../../constants/strings";

import { getSupportTickets, updateTicketStatus } from "../../services/api";

import "../table-common.css";

const ReportsPage = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // ================= FETCH =================
  const fetchReports = async () => {
    try {
      setLoading(true);

      const res = await getSupportTickets();
      console.log(res);
      setRows(res.data.data || []);
    } catch (err: any) {
      alert(err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  // ================= STATUS UPDATE =================
  const handleStatusChange = async (id: string, status: string) => {
    try {
      // 🔥 optimistic update
      setRows((prev) =>
        prev.map((row) => (row._id === id ? { ...row, status } : row)),
      );

      await updateTicketStatus(id, status);
    } catch (err: any) {
      alert(err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG);
      fetchReports(); // rollback
    }
  };

  // ================= FILTER =================
  const filteredRows = rows.filter((row) => {
    const matchSearch = Object.values(row)
      .join(" ")
      .toLowerCase()
      .includes(searchText.toLowerCase());

    const matchType = typeFilter ? row.type === typeFilter : true;

    return matchSearch && matchType;
  });

  // ================= COLUMNS =================
  const columns: GridColDef[] = [
    // 👤 USER
    {
      field: "user",
      headerName: REPORT_STRINGS.COLUMN_NAME,
      flex: 1.5,
      renderCell: (params) => params.row.user?.name?.trim() || "Guest User",
    },

    // 📝 TYPE
    {
      field: "Contact No",
      headerName: REPORT_STRINGS.COLUMN_PHONE,
      flex: 1,
      renderCell: (params) => params.row.user?.mobile?.trim() || "N/A",
    },

    // 📄 DESCRIPTION (🔥 NEW)
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      renderCell: (params) =>
        params.row.description?.trim() || "No Description",
    },

    // 📅 DATE
    {
      field: "createdAt",
      headerName: REPORT_STRINGS.COLUMN_DATE,
      flex: 1,
      renderCell: (params) =>
        new Date(params.value).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },

    // 📊 STATUS
    {
      field: "status",
      headerName: REPORT_STRINGS.COLUMN_STATUS,
      flex: 1,
      renderCell: (params) => <StatusChip status={params.value} />,
    },

    // ⚙️ ACTIONS
    {
      field: "actions",
      headerName: REPORT_STRINGS.COLUMN_ACTIONS,
      flex: 2,
      sortable: false,
      align: "center",
      headerAlign: "center",

      renderCell: (params) => {
        const currentStatus = params.row.status;

        return (
          <Box className="action-cell">
            <Stack direction="row" spacing={0.5}>
              {/* 🔵 IN PROGRESS */}
              <Tooltip
                title={
                  currentStatus === "in_progress"
                    ? "Already in progress"
                    : "Mark as In Progress"
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={currentStatus === "in_progress"}
                    onClick={() =>
                      handleStatusChange(params.row._id, "in_progress")
                    }
                    sx={{
                      minWidth: "32px",
                      padding: "6px",
                      color: "var(--blue)",
                      borderColor: "var(--blue)",
                    }}
                  >
                    <AutorenewIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>

              {/* 🟢 RESOLVED */}
              <Tooltip
                title={
                  currentStatus === "resolved"
                    ? "Already resolved"
                    : "Mark as Resolved"
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={currentStatus === "resolved"}
                    onClick={() =>
                      handleStatusChange(params.row._id, "resolved")
                    }
                    sx={{
                      minWidth: "32px",
                      padding: "6px",
                      backgroundColor: "var(--success)",
                    }}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <PageHeader
        title={REPORT_STRINGS.TITLE}
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder={REPORT_STRINGS.SEARCH_PLACEHOLDER}
        filterValue={typeFilter}
        onFilterChange={setTypeFilter}
        filterOptions={[
          { label: REPORT_STRINGS.FILTER_TYPE, value: "" },
          { label: "Bug", value: "bug" },
          { label: "Payment", value: "payment" },
          { label: "Provider", value: "provider" },
        ]}
      />

      <DataTable
        rows={filteredRows}
        columns={columns}
        loading={loading}
        getRowId={(row) => row._id}
        pageSizeOptions={[10, 20, 50]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
      />
    </Box>
  );
};

export default ReportsPage;
